import { IMapZoneCreateDTO, IMapZoneDetailDTO, IMapZoneUpdateBaseDTO, IMapZoneUpdateDTO, MapZoneCreateDTO, MapZonesClient, MapZoneUpdateDTO } from 'api/swagger/ApiClient';
import { autoinject, BindingEngine, Container, newInstance } from 'aurelia-framework';
import { Disposable } from 'framework/aurelia_tools/binding-helper';
import { EventHelper } from 'framework/aurelia_tools/event-helper';
import { tryExecute } from 'framework/error_handling/error-utils';
import { TaskCounter } from 'framework/script_utilities/task-executor';
import * as L from 'leaflet';
import { MapZonesFilter } from 'model/filters/map-zones-filter';
import { MapZoneDetailModel, MapZonesQueryMemory } from 'model/map-zones';
import * as E from 'support/errors';
import { BATCH_SIZE } from 'support/settings';

@autoinject
export class MapZonesController {
  public queryMemory: MapZonesQueryMemory;
  public mapZones: MapZoneDetailModel[] = [];
  public loaded = false;
  public mapCenter: L.LatLng = null;
  public mapZoom: number = null;
  public detailedMapZone: MapZoneDetailModel = null;
  public addMapZoneInMap: (mapZone: MapZoneDetailModel) => void;
  public removeMapZoneInMap: (mapZone: MapZoneDetailModel) => void;
  public detailMapZoneInMap: (mapZone: MapZoneDetailModel) => void;
  public undetailMapZoneInMap: (mapZone: MapZoneDetailModel) => void;
  public zoomMapZoneInMap: (mapZone: MapZoneDetailModel) => void;
  public setSplitter: () => void;
  public storeSplitter: () => void;

  private lastQueryId = 0;
  private sortExpressionSubscription: Disposable = null;

  constructor(container: Container, private bindingEngine: BindingEngine, private api: MapZonesClient, @newInstance() public taskCounter: TaskCounter, private eventHelper: EventHelper) {
    this.queryMemory = new MapZonesQueryMemory();
    this.queryMemory.initialize(MapZonesFilter);
    container.registerInstance(MapZonesQueryMemory, this.queryMemory);
  }

  /* Lifecycle methods */

  public async attach() {
    this.sortExpressionSubscription = this.bindingEngine.propertyObserver(this.queryMemory, 'sortExpression').subscribe(() => this.sortExpressionChanged());
  }

  public detach() {
    this.sortExpressionSubscription.dispose();
  }

  /* Data loading methods */

  public async search() {
    if (await this.requestMapZoneCancelChanges()) {
      this.unsetDetailedMapZone();
      await this.taskCounter.execute(() => tryExecute(() => this.queryMapZones(), E.FILTER_ERROR_MESSAGE, E.FILTER_ERROR_TITLE, true));
    }
  }

  private async queryMapZones() {
    const queryId = ++this.lastQueryId;
    this.mapZones.forEach(mapZone => {
      this.removeMapZoneInMap(mapZone);
    })
    const mapZones = await this.fetchMapZones(0, this.batchSize);
    if (queryId === this.lastQueryId) {
      this.mapZones = mapZones;
      this.loaded = true;
    }
  }

  public async fetchMapZones(skip: number, take: number) {
    const dataResult = await this.api.getAllByFilter(skip, take, this.queryMemory.sortExpression, this.queryMemory.filter.text, this.queryMemory.filter.includeArchived ? null : true);
    const mapZones = dataResult.data.map(x => this.mapToMapZoneDetailModel(x));
    mapZones.forEach(mapZone => {
      this.addMapZoneInMap(mapZone);
    });
    return mapZones;
  }

  /* Map zone functions */

  public async detailMapZone(mapZone: MapZoneDetailModel) {
    if (await this.requestMapZoneCancelChanges()) {
      this.unsetDetailedMapZone();
      this.setDetailedMapZone(mapZone);
      this.zoomMapZoneInMap(mapZone);
    }
  }

  public async createNewMapZone(): Promise<void> {
    if (await this.requestMapZoneCancelChanges()) {
      this.unsetDetailedMapZone();
      const mapZone = {
        data: {
          id: null,
          name: null,
          featureCollection: null,
          createdBy: null,
          createdOn: null,
          modifiedBy: null,
          modifiedOn: null,
          archivedBy: null,
          archivedOn: null,
          isActive: true,
        } as IMapZoneDetailDTO,
        featureCollectionLayer: L.geoJSON(),
        isDetailed: false,
        isHighlighted: false,
        isEdited: false,
      } as MapZoneDetailModel;
      mapZone.data.featureCollection = JSON.stringify(mapZone.featureCollectionLayer.toGeoJSON());
      this.mapZones.unshift(mapZone);
      this.addMapZoneInMap(mapZone);
      this.setDetailedMapZone(mapZone);
    }
  }

  public async saveMapZone(mapZone: MapZoneDetailModel): Promise<MapZoneDetailModel> {
    const data = this.mapToIMapZoneUpdateBaseDTO(mapZone.data);
    if (mapZone.data.id)
      mapZone.data = await this.api.updateMapZone(mapZone.data.id, new MapZoneUpdateDTO(data as IMapZoneUpdateDTO));
    else
      mapZone.data = await this.api.createMapZone(new MapZoneCreateDTO(data as IMapZoneCreateDTO));
    this.eventHelper.publish('settings-changed');
    this.refreshMapZone(mapZone);
    return mapZone;
  }

  public cancelNewMapZone() {
    const mapZoneIndex = this.mapZones.findIndex(x => x.data.id == null);
    const mapZone = this.mapZones[mapZoneIndex];
    this.mapZones.splice(mapZoneIndex, 1);
    this.unsetDetailedMapZone();
    this.removeMapZoneInMap(mapZone);
  }

  public cancelMapZoneChanges(mapZone: MapZoneDetailModel) {
    this.refreshMapZone(mapZone);
  }

  public async requestMapZoneCancelChanges(): Promise<boolean> {
    return this.detailedMapZone == null || await this.detailedMapZone.requestCancelChanges();
  }

  public async archiveMapZone(mapZone: MapZoneDetailModel): Promise<MapZoneDetailModel> {
    if (await this.requestMapZoneCancelChanges()) {
      const archivedMapZoneData = await this.api.archiveMapZone(mapZone.data.id);
      if (!this.queryMemory.filter.includeArchived) {
        if (this.detailedMapZone != null && this.detailedMapZone.data.id === mapZone.data.id)
          this.unsetDetailedMapZone();
        this.mapZones.splice(this.mapZones.indexOf(mapZone), 1);
        this.removeMapZoneInMap(mapZone);
      }
      else {
        mapZone.data = archivedMapZoneData;
        if (this.detailedMapZone != null && this.detailedMapZone.data.id === mapZone.data.id) {
          this.unsetDetailedMapZone();
          this.setDetailedMapZone(mapZone);
        }
      }
    }
    return mapZone;
  }

  public async restoreMapZone(mapZone: MapZoneDetailModel): Promise<MapZoneDetailModel> {
    if (await this.requestMapZoneCancelChanges()) {
      mapZone.data = await this.api.unarchiveMapZone(mapZone.data.id);
      if (this.detailedMapZone != null && this.detailedMapZone.data.id === mapZone.data.id) {
        this.unsetDetailedMapZone();
        this.setDetailedMapZone(mapZone);
      }
    }
    return mapZone;
  }

  /* Helper methods */

  private refreshMapZone(mapZone: MapZoneDetailModel) {
    this.unsetDetailedMapZone();
    this.removeMapZoneInMap(mapZone);
    mapZone.featureCollectionLayer = L.geoJSON(JSON.parse(mapZone.data.featureCollection));
    this.addMapZoneInMap(mapZone);
    this.setDetailedMapZone(mapZone);
  }

  private setDetailedMapZone(mapZone: MapZoneDetailModel) {
    this.detailedMapZone = mapZone;
    mapZone.isDetailed = true;
    this.detailMapZoneInMap(mapZone);
  }

  private unsetDetailedMapZone() {
    if (this.detailedMapZone != null) {
      this.detailedMapZone.isDetailed = false;
      this.undetailMapZoneInMap(this.detailedMapZone);
      this.detailedMapZone = null;
    }
  }

  private async sortExpressionChanged() {
    this.taskCounter.execute(() => tryExecute(() => this.queryMapZones(), E.SORT_ERROR_MESSAGE, E.SORT_ERROR_TITLE, true));
  }

  private mapToMapZoneDetailModel(dto: IMapZoneDetailDTO): MapZoneDetailModel {
    return {
      data: dto,
      featureCollectionLayer: L.geoJSON(JSON.parse(dto.featureCollection), {}),
      isHighlighted: false,
      isDetailed: false,
      isEdited: false,
    } as MapZoneDetailModel;
  }

  private mapToIMapZoneUpdateBaseDTO(mapZone: IMapZoneDetailDTO): IMapZoneUpdateBaseDTO {
    return {
      name: mapZone.name,
      featureCollection: mapZone.featureCollection
    } as IMapZoneUpdateBaseDTO;
  }

  get batchSize() {
    return BATCH_SIZE;
  }
}
